/**
 * Report History Hook
 * Business logic for report task history with pagination
 */
import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getReportHistory,
    getReportWaf,
    downloadReport,
    getCachedManifest,
} from '../services'
import type { ReportWAFTask } from '../types/response'
import {
    getHistoryColumns,
    getHistoryActions,
} from '../utils/report-history-config'

export const useReportHistory = () => {
    const { t } = useTranslation('report_waf')
    const { id: reportUuid } = useParams<{ id: string }>()

    const { params, setParams } = useUrlSearchParams({
        defaults: { page: 1, page_size: 10 },
    })

    // Fetch report detail
    const { data: report } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REPORT_WAF, 'detail', reportUuid],
        queryFn: () => getReportWaf(reportUuid!),
        enabled: !!reportUuid,
    })

    // Fetch history with pagination
    const {
        data: historyData,
        renderState,
        refetch,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REPORT_WAF, 'history', reportUuid, params],
        queryFn: () =>
            getReportHistory(
                { page: params.page, page_size: params.page_size, q: '' },
                reportUuid!
            ),
        enabled: !!reportUuid,
    })

    const tasks = historyData?.results || []
    const pagination = historyData?.pagination
    const customerName = report?.result?.customer_name || ''
    const customerId = report?.result?.customer_id
    // Engine comes from report_attribute.report_engine (LEFT JOIN in the
    // Rails `reports` scope). Default to v1 to keep behaviour sane for
    // legacy rows where the column was backfilled empty.
    const reportEngine = report?.result?.report_engine || 'v1'

    // Pagination handlers
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (size: number) => setParams({ page_size: size }),
        [setParams]
    )

    // Handlers
    // View a specific history row by resolving the manifest UUID from
    // the @v2 cached endpoint
    // (GET /api/v2/reports/cached/{specific}/{task_referrer_jid}), then
    // opening report.vnetwork.dev at the versioned snapshot path
    // (/v1/reports/{uuid} or /v2/reports/{uuid}). The old inline URL
    // passed gte/lte as query params to a report UI route that doesn't
    // exist, so nothing rendered.
    const handleView = useCallback(
        async (row: ReportWAFTask) => {
            if (!row.specific || !row.task_referrer_jid) {
                toast.error(t('errors.file_unavailable'))
                return
            }
            try {
                const { uuid } = await getCachedManifest(
                    row.specific,
                    row.task_referrer_jid
                )
                if (!uuid) {
                    toast.error(t('errors.file_unavailable'))
                    return
                }
                const base = import.meta.env.APP_REPORT_URL
                const viewUrl = `${base}/${reportEngine}/reports/${uuid}`
                window.open(viewUrl, '_blank')
            } catch {
                toast.error(t('errors.file_unavailable'))
            }
        },
        [reportEngine, t]
    )

    const handleDownload = useCallback(
        async (row: ReportWAFTask) => {
            try {
                await downloadReport(reportUuid!, row.id)
            } catch {
                toast.error(t('errors.file_unavailable'))
            }
        },
        [reportUuid, t]
    )

    // Columns & actions
    const columns = useMemo(() => getHistoryColumns(t), [t])
    const actions = useMemo(
        () =>
            getHistoryActions(t, {
                onView: handleView,
                onDownload: handleDownload,
            }),
        [t, handleView, handleDownload]
    )

    return {
        reportUuid,
        customerName,
        customerId,
        tasks,
        pagination,
        params,
        stateComponent: renderState(),
        refetch,
        columns,
        actions,
        handlePageChange,
        handlePageSizeChange,
        t,
    }
}
